import {Injectable, EventEmitter} from '@angular/core';
import {firstValueFrom} from 'rxjs';
import {SankhyaGatewayService} from './sankhya-gateway.service';
import {AuthService} from './auth.service';

export interface EmpresaOpcao {
    codEmp: string;
    nomeFant: string;
    razaoAbrev: string;
    /** true = empresa VIRTUAL (GPL_EMPRESA_VIRTUAL, ex.: Curitiba/BH/Rio Verde) —
     *  aparece na mesma lista das empresas reais, mas por baixo dos panos usa
     *  codEmpReal (CODEMP nativo) + codLocal (Local fixo daquela filial). */
    virtual?: boolean;
    /** Só presente quando virtual=true — CODEMP real (TSIEMP) por trás da empresa virtual. */
    codEmpReal?: string;
    /** Só presente quando virtual=true — CODLOCAL fixo daquela empresa virtual. */
    codLocal?: string;
    /** CNPJ da empresa (TSIEMP.CGC) — usado para achar o parceiro/cliente da própria empresa. */
    cnpj?: string;
    /** Código do parceiro (cliente) cadastrado que representa a empresa (virtuais: GPL_EMPRESA_VIRTUAL.CODPARC). */
    codParcEmpresa?: number;
}

@Injectable({providedIn: 'root'})
export class EmpresaStateService {

    opcoes: EmpresaOpcao[] = [];
    carregando = false;

    /** Chave usada para lembrar a última empresa selecionada neste navegador —
     *  sem isso, a seleção era um estado só em memória (nunca persistido) e
     *  voltava pro padrão (menor CODEMP) a cada F5, expiração de sessão ou
     *  novo login. Como Separação/Conferência/Logística agora FILTRAM
     *  estritamente pela empresa selecionada (ver EstoqueSeparacao/
     *  Conferencia/Logistica e os Painéis), esse reset silencioso fazia um
     *  pedido "sumir" de uma etapa pra outra sempre que o navegador recarregava
     *  entre elas e a empresa voltava pra outra diferente da do pedido. */
    private static readonly STORAGE_KEY = 'gpl_empresa_selecionada';

    private _selecionada: string = '';
    public empresaMudou = new EventEmitter<string>();

    get selecionada(): string {
        return this._selecionada;
    }

    set selecionada(val: string) {
        if (this._selecionada !== val) {
            this._selecionada = val;
            try {
                localStorage.setItem(EmpresaStateService.STORAGE_KEY, val);
            } catch {
                // localStorage indisponível (modo privado, etc.) — segue só em memória.
            }
            this.empresaMudou.emit(val);
        }
    }

    /** Guarda opcional que intercepta a troca de empresa ANTES dela acontecer
     *  -- registrada por telas que precisam confirmar com o usuário antes de
     *  deixar trocar (ex.: Pedido de Venda com um pedido em edição/lançamento,
     *  onde trocar de empresa muda o Local usado pro cálculo de estoque
     *  Disponível). Retorna true = pode trocar na hora; false = quem
     *  registrou vai decidir depois (mostrar confirmação) e aplicar a troca
     *  manualmente (via `selecionada = ...`) se o usuário confirmar. */
    private _guardaTroca: ((novoCodEmp: string) => boolean) | null = null;

    registrarGuardaTroca(fn: ((novoCodEmp: string) => boolean) | null): void {
        this._guardaTroca = fn;
    }

    /** Ponto de entrada usado pelo seletor de empresa do MENU -- passa pelo
     *  guard registrado (se houver) antes de aplicar a troca de verdade. */
    solicitarTroca(codEmp: string): void {
        if (codEmp === this._selecionada) return;
        if (this._guardaTroca && !this._guardaTroca(codEmp)) {
            return;
        }
        this.selecionada = codEmp;
    }

    /** Opção completa (com virtual/codEmpReal/codLocal) da empresa SELECIONADA. */
    get opcaoSelecionada(): EmpresaOpcao | undefined {
        return this.opcoes.find(o => o.codEmp === this._selecionada);
    }

    /** true quando a empresa selecionada é uma empresa VIRTUAL (Curitiba/BH/Rio Verde). */
    get selecionadaEhVirtual(): boolean {
        return !!this.opcaoSelecionada?.virtual;
    }

    /** CODEMP REAL (nativo TSIEMP) por trás da seleção atual — use isso (em vez de
     *  `selecionada`) em qualquer lugar que precise gravar/filtrar TGFCAB.CODEMP
     *  de verdade (ex.: criar Pedido de Venda). Empresa real selecionada: é ela
     *  mesma. Empresa virtual selecionada: resolve pro codEmpReal por trás dela. */
    get codEmpReal(): string {
        const opcao = this.opcaoSelecionada;
        return (opcao?.virtual && opcao.codEmpReal) ? opcao.codEmpReal : this._selecionada;
    }

    /** Mapa de cores por CODEMP (Delphi BGR → CSS RGB). */
    readonly CORES: Record<string, string> = {
        '1': '#FFFFFF',
        '2': '#D98D8B',
        '3': '#FFC000',
        '4': '#92D050',
        '5': '#AE99C7',
        '6': '#FCD4B4',
        '7': '#AEDCE8',
        '100': '#6185B1',
        // Empresas virtuais (GPL_EMPRESA_VIRTUAL) — cores próprias pra
        // distinguir visualmente das empresas reais na lista/seletor.
        '8':  '#8FBC8F', // Curitiba
        '9':  '#E9967A', // BH
        '10': '#87CEEB', // Rio Verde
        '12': '#A3B8E0', // Aero LLC (Importação)
    };

    get cor(): string {
        return this.CORES[this.selecionada] ?? '#F0F4F8';
    }

    /** Cor da empresa pelo codEmp (independente do selecionado). */
    corPorCodEmp(codEmp: string | number): string {
        return this.CORES[String(codEmp)] ?? '#F0F4F8';
    }

    /** Cor misturada com branco (fator 0–1: 0 = cor pura, 1 = branco). */
    private _misturarComBranco(hexCor: string, fator: number): string {
        const hex = hexCor.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        const rr = Math.round(r + (255 - r) * fator).toString(16).padStart(2, '0');
        const gg = Math.round(g + (255 - g) * fator).toString(16).padStart(2, '0');
        const bb = Math.round(b + (255 - b) * fator).toString(16).padStart(2, '0');
        return `#${rr}${gg}${bb}`;
    }

    /** Cor clara da empresa SELECIONADA misturada com branco. */
    corClara(fator = 0.55): string {
        return this._misturarComBranco(this.cor, fator);
    }

    /** Cor clara de qualquer empresa pelo codEmp, misturada com branco. */
    corClaraPorCodEmp(codEmp: string | number, fator = 0.78): string {
        return this._misturarComBranco(this.corPorCodEmp(codEmp), fator);
    }

    constructor(
        private readonly sankhyaGateway: SankhyaGatewayService,
        private readonly authService: AuthService,
    ) {
    }

    async carregar(): Promise<void> {
        if (this.opcoes.length > 0 || this.carregando) return; // já carregado
        this.carregando = true;
        try {
            const res: any = await firstValueFrom(this.sankhyaGateway.buscarEmpresasPedidoVenda());
            const body = res?.responseBody ?? res;
            const lista: any[] = body?.empresas ?? [];
            if (body?.sucesso && Array.isArray(lista) && lista.length > 0) {
                this.opcoes = lista.map((e: any) => ({
                    codEmp: String(e.codEmp),
                    nomeFant: e.nomeFant ?? '',
                    razaoAbrev: e.razaoAbrev ?? '',
                    virtual: !!e.virtual,
                    codEmpReal: e.codEmpReal != null ? String(e.codEmpReal) : undefined,
                    codLocal: e.codLocal != null ? String(e.codLocal) : undefined,
                    cnpj: e.cnpj != null ? String(e.cnpj) : undefined,
                    codParcEmpresa: Number(e.codParcEmpresa) > 0 ? Number(e.codParcEmpresa) : undefined,
                }));
                if (!this.selecionada) {
                    // Recupera a última empresa escolhida neste navegador (se ainda for
                    // uma opção válida); só cai pro padrão na primeira vez que este
                    // navegador é usado, ou se a empresa salva não existe mais na lista.
                    let salva = '';
                    try {
                        salva = localStorage.getItem(EmpresaStateService.STORAGE_KEY) ?? '';
                    } catch {
                        // localStorage indisponível — ignora e usa o padrão abaixo.
                    }
                    const salvaValida = salva && this.opcoes.some(o => o.codEmp === salva);
                    if (salvaValida) {
                        this.selecionada = salva;
                    } else {
                        // Primeiro acesso deste navegador (ou empresa salva não existe
                        // mais): tenta pré-selecionar a empresa "padrão" do usuário
                        // (TSIUSU.CODEMP nativo) antes de cair no fallback antigo
                        // (primeira empresa da lista). O usuário continua podendo
                        // trocar manualmente a qualquer momento — isso só define o
                        // ponto de partida.
                        let codEmpUsuario: number | null = null;
                        try {
                            codEmpUsuario = await this.authService.getCodEmpUsuario();
                        } catch {
                            codEmpUsuario = null;
                        }
                        const doUsuarioValida = codEmpUsuario != null
                            && this.opcoes.some(o => o.codEmp === String(codEmpUsuario));
                        this.selecionada = doUsuarioValida ? String(codEmpUsuario) : this.opcoes[0].codEmp;
                    }
                }
            }
        } catch (err) {
            console.error('[EmpresaState] erro ao carregar empresas:', err);
        } finally {
            this.carregando = false;
        }
    }
}
